import { useState, useRef, useCallback, useMemo, memo } from 'react';
import { useStore, calcLineas, formatEur, today } from '../store';
import { Btn, Badge, Modal, Input, Select, StatCard, SearchBar, useSearch, useLocalStorage, LineasEditor, Confirm, ClienteSelector, MultiSelect, useSortable, SortableTh, EstadoQuick, useT, THEME, pastelBtn } from './UI';
import { printFactura, printTicket } from '../utils/print';
import { exportFacturas } from '../utils/exportExcel';
import * as XLSX from 'xlsx';

function termicaPedido(doc) {
  return fetch('/api/imprimir-termica', {
    method: 'POST',
    headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ tipo: 'ticket', rep: { ...doc, tipo: 'PEDIDO CLIENTE' } })
  }).then(r => r.json()).then(d => { if (!d.ok) throw new Error(d.error); });
}

const PAGOS   = ['Efectivo','Tarjeta','Transferencia','30 días','60 días','Bizum'];
const PAGOS_CON_MIXTO = [...PAGOS, 'Mixto'];
const ESTADOS = ['Cobrada','Pendiente','Anulada'];
const ESTADOS_PRESUP = ['Pendiente','Enviado','Aceptado','Rechazado','Anulado'];
const ESTADOS_PEDIDO_CLI = ['Pendiente','Encargado al proveedor','Recibido','Entregado al cliente','Anulado'];

function estadoColors(theme) {
  return theme === THEME.dark
    ? { Cobrada:'rgba(16,185,129,0.16)',Pendiente:'rgba(245,158,11,0.16)',Anulada:'rgba(239,68,68,0.16)',Anulado:'rgba(239,68,68,0.16)',Aceptado:'rgba(16,185,129,0.16)',Enviado:'rgba(59,130,246,0.16)',Rechazado:'rgba(239,68,68,0.16)','Encargado al proveedor':'rgba(167,139,250,0.18)',Recibido:'rgba(59,130,246,0.16)','Entregado al cliente':theme.surface2 }
    : { Cobrada:'#dcfce7',Pendiente:'#fef3c7',Anulada:'#fee2e2',Anulado:'#fee2e2',Aceptado:'#dcfce7',Enviado:'#dbeafe',Rechazado:'#fee2e2','Encargado al proveedor':'#ede9fe',Recibido:'#dbeafe','Entregado al cliente':theme.surface2 };
}
function estadoTexts(theme) {
  return theme === THEME.dark
    ? { Cobrada:'#6ee7b7',Pendiente:'#fbbf24',Anulada:'#fca5a5',Anulado:'#fca5a5',Aceptado:'#6ee7b7',Enviado:'#93c5fd',Rechazado:'#fca5a5','Encargado al proveedor':'#c4b5fd',Recibido:'#93c5fd','Entregado al cliente':theme.body }
    : { Cobrada:'#166534',Pendiente:'#92400e',Anulada:'#991b1b',Anulado:'#991b1b',Aceptado:'#166534',Enviado:'#1e40af',Rechazado:'#991b1b','Encargado al proveedor':'#5b21b6',Recibido:'#1e40af','Entregado al cliente':theme.body };
}

function statusBadge(e) {
  const m = { Cobrada:'green', Pendiente:'amber', Anulada:'red', Aceptado:'green', Enviado:'blue', Rechazado:'red' };
  return <Badge variant={m[e]||'gray'}>{e}</Badge>;
}

function VFBadge({ doc }) {
  const theme = useT();
  const est = doc.vf_estado;
  if (!est) return null;
  const map = {
    OK:        { label:'VF ✓', bg:'#dcfce7', color:'#15803d' },
    PENDIENTE: { label:'VF ⏳', bg:'#fef9c3', color:'#92400e' },
    ERROR:     { label:'VF ✗', bg:'#fee2e2', color:'#991b1b' },
  };
  const s = map[est] || { label: est, bg:'#f3f4f6', color:theme.body };
  return (
    <span title={doc.vf_csv || doc.vf_estado} style={{
      fontSize:10, fontWeight:700, padding:'2px 5px',
      borderRadius:4, background:s.bg, color:s.color, marginLeft:4,
    }}>{s.label}</span>
  );
}

function VFDetalle({ doc, col, onReenviado }) {
  const theme = useT();
  if (!doc.vf_huella && doc.vf_estado !== 'ERROR') return null;

  async function reenviar() {
    const token = localStorage.getItem('ri_token');
    await fetch(`/api/verifactu/reenviar/${col}/${doc.id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    onReenviado?.();
  }

  return (
    <div style={{ marginTop:16, padding:12, background:theme.surface2, borderRadius:8, border:`1px solid ${theme.border}` }}>
      <div style={{ fontSize:11, fontWeight:700, color:theme.body, marginBottom:6 }}>VERI*FACTU</div>
      {doc.vf_huella && (
        <div style={{ fontSize:11, color:theme.muted, marginBottom:4 }}>
          Huella: <code style={{ fontFamily:'monospace', fontSize:10 }}>{doc.vf_huella.substring(0,16)}…</code>
        </div>
      )}
      {doc.vf_csv && (
        <div style={{ fontSize:11, color:'#16a34a', marginBottom:4 }}>CSV AEAT: <b>{doc.vf_csv}</b></div>
      )}
      {doc.vf_timestamp && (
        <div style={{ fontSize:10, color:theme.faint, marginBottom:8 }}>Enviado: {doc.vf_timestamp}</div>
      )}
      {(doc.vf_estado === 'ERROR' || doc.vf_estado === 'PENDIENTE') && (
        <Btn size="sm" onClick={reenviar}>Reenviar a AEAT</Btn>
      )}
    </div>
  );
}

function newDoc(tipo, cfg) {
  const estadoDefault = tipo === 'PRESUPUESTO' ? 'Pendiente' : 'Cobrada';
  return { id:null, tipo, num:'', fecha:today(), clienteId:'', clienteNombre:'', clienteNif:'', clienteDireccion:'', clienteTel:'', pago:'Tarjeta', validez:'', lineas:[{ desc:'', cant:1, precio:0, iva:cfg.iva }], estado:estadoDefault, senal:'', observaciones:'' };
}

const TIPO_CONFIG = {
  facturas:     { label:'Facturas',     singular:'Factura',     sig:'sigFactura',     tipo:'FACTURA',     fmt: (n,y) => `${n}_${y}` },
  proformas:    { label:'Proformas',    singular:'Proforma',    sig:'sigProforma',    tipo:'PROFORMA',    fmt: (n,y) => `PR-${n}_${y}` },
  presupuestos:    { label:'Presupuestos',       singular:'Presupuesto',      sig:'sigPresupuesto',    tipo:'PRESUPUESTO',    fmt: (n,y) => `P-${n}_${y}` },
  pedidosCliente:  { label:'Pedidos de cliente',  singular:'Pedido de cliente', sig:'sigPedidoCliente',  tipo:'PEDIDO CLIENTE', fmt: (n,y) => `PC-${n}_${y}` },
};

// Memoizado aparte: así no se re-renderiza (con sus filas) en cada tecla escrita
// en el formulario del modal — solo cuando cambian sus props.
const TablaDocumentos = memo(function TablaDocumentos({
  shownItems, sortField, sortDir, handleSort, theme, tipoKey, cfg, config, replaceItem, setConfirm,
  onEdit, onDescargarPdf, onEnviarEmail, onEnviarWhatsapp, onEnviarWhatsappPedidoListo,
  onDuplicar, onPasarAFactura, onPasarATicket, onConvertirAFactura, onPasarAPedidoCliente,
}) {
  return (
    <div style={{ background:theme.surface, borderRadius:12, border:`1px solid ${theme.border}`, overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead>
          <tr style={{ background:theme.surface2 }}>
            {[['Nº','num'],['Fecha','fecha'],['Cliente','clienteNombre'],['NIF/CIF','clienteNif'],['Base',null],['IVA',null],['Total',null],['Estado','estado'],...(tipoKey==='facturas'?[['Pago','pago']]:[]),[''  ,null]].map(([label,field]) => (
              <SortableTh key={label+field} label={label} field={field} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {shownItems.map((f, i) => {
            const { base, iva, total } = calcLineas(f.lineas);
            return (
              <tr key={f.id} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                <td style={{ padding:'10px 14px', fontWeight:600, color:'#1a56db' }}>{f.num}</td>
                <td style={{ padding:'10px 14px' }}>{new Date(f.fecha).toLocaleDateString('es-ES')}</td>
                <td style={{ padding:'10px 14px', fontWeight:500 }}>{f.clienteNombre}</td>
                <td style={{ padding:'10px 14px', color:theme.muted }}>{f.clienteNif||'—'}</td>
                <td style={{ padding:'10px 14px' }}>{formatEur(base)}</td>
                <td style={{ padding:'10px 14px' }}>{formatEur(iva)}</td>
                <td style={{ padding:'10px 14px', fontWeight:600 }}>
                  {formatEur(total)}
                  {tipoKey === 'pedidosCliente' && parseFloat(f.senal) > 0 && (
                    <div style={{ fontSize:11, fontWeight:500, marginTop:2 }}>
                      <span style={{ color:'#0e9f6e' }}>Señal: {formatEur(parseFloat(f.senal))}</span>
                      <span style={{ color:theme.faint }}> · </span>
                      <span style={{ color:'#c27803' }}>Resto: {formatEur(Math.max(0, total - parseFloat(f.senal)))}</span>
                    </div>
                  )}
                </td>
                <td style={{ padding:'10px 14px', position:'relative' }}>
                  <EstadoQuick
                    estado={f.estado}
                    estados={tipoKey==='presupuestos' ? ESTADOS_PRESUP : tipoKey==='pedidosCliente' ? ESTADOS_PEDIDO_CLI : ESTADOS}
                    colores={estadoColors(theme)} textos={estadoTexts(theme)}
                    onChange={v => replaceItem(tipoKey, { ...f, estado: v })}
                  />
                  {(tipoKey === 'facturas' || tipoKey === 'tickets') && <VFBadge doc={f} />}
                  {f.facturaGenerada && (
                    <span style={{ marginLeft:4, fontSize:10, background: theme===THEME.dark ? 'rgba(16,185,129,0.16)' : '#d1fae5', color: theme===THEME.dark ? '#6ee7b7' : '#065f46', borderRadius:20, padding:'1px 6px', fontWeight:700 }}>
                      → {f.facturaGenerada}
                    </span>
                  )}
                </td>
                {tipoKey === 'facturas' && (
                  <td style={{ padding:'10px 14px', position:'relative' }}>
                    <EstadoQuick
                      estado={f.pago || 'Tarjeta'}
                      estados={PAGOS}
                      colores={{ Efectivo:'#dcfce7', Tarjeta:'#dbeafe', Transferencia:'#ede9fe', Bizum:'#fef3c7', '30 días':'#f3f4f6', '60 días':'#f3f4f6' }}
                      textos={{ Efectivo:'#166534', Tarjeta:'#1e40af', Transferencia:'#5b21b6', Bizum:'#92400e', '30 días':'#374151', '60 días':'#374151' }}
                      onChange={v => replaceItem(tipoKey, { ...f, pago: v })}
                    />
                  </td>
                )}
                <td style={{ padding:'10px 14px' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    <Btn size="sm" variant="ghost" title="Editar" onClick={() => onEdit(f)}>✏️</Btn>
                    <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'blue') }} title="Imprimir en A4" onClick={() => printFactura({...f, tipo:cfg.tipo}, config)}>🖨️ A4</Btn>
                    {(tipoKey === 'facturas' || tipoKey === 'presupuestos' || tipoKey === 'proformas') && (
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'purple') }} title="Descargar PDF" onClick={() => onDescargarPdf(f)}>📄 PDF</Btn>
                    )}
                    {(tipoKey === 'facturas' || tipoKey === 'presupuestos' || tipoKey === 'proformas') && (
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'orange') }} title="Enviar por email" onClick={() => onEnviarEmail(f)}>✉️ Email</Btn>
                    )}
                    {(tipoKey === 'facturas' || tipoKey === 'presupuestos' || tipoKey === 'proformas') && (
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Enviar PDF por WhatsApp" onClick={() => onEnviarWhatsapp(f)}>💬 WA</Btn>
                    )}
                    {tipoKey === 'pedidosCliente' && (
                      <Btn size="sm" variant="ghost" title="Imprimir en impresora térmica" onClick={() => termicaPedido(f).catch(e => { alert('Error impresora: '+e.message+'\n\nAbriendo ventana...'); printTicket({...f, tipo:'PEDIDO CLIENTE', pago:f.pago||'Pendiente'}, config); })}>🖨️</Btn>
                    )}
                    {tipoKey === 'pedidosCliente' && (
                      <Btn size="sm" variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Avisar por WhatsApp: pedido recibido, listo para recoger" onClick={() => onEnviarWhatsappPedidoListo(f)}>💬 WA</Btn>
                    )}
                    {tipoKey === 'presupuestos' && (
                      <Btn size="sm" variant="ghost" onClick={() => onDuplicar(f)} title="Duplicar presupuesto" style={{ color:'#7c3aed' }}>⧉</Btn>
                    )}
                    {tipoKey === 'pedidosCliente' && !f.facturaGenerada && !f.ticketGenerado && f.estado !== 'Anulado' && (
                      <Btn size="sm" variant="success" onClick={() => onPasarAFactura(f)} title="Convertir a factura">📄→F</Btn>
                    )}
                    {tipoKey === 'pedidosCliente' && !f.facturaGenerada && !f.ticketGenerado && f.estado !== 'Anulado' && (
                      <Btn size="sm" variant="success" onClick={() => onPasarATicket(f)} title="Convertir a ticket" style={{ background:'#0e9f6e' }}>🧾→T</Btn>
                    )}
                    {tipoKey === 'pedidosCliente' && (f.facturaGenerada || f.ticketGenerado) && (
                      <span style={{ fontSize:10, background: theme===THEME.dark ? 'rgba(16,185,129,0.16)' : '#d1fae5', color: theme===THEME.dark ? '#6ee7b7' : '#065f46', borderRadius:20, padding:'2px 7px', fontWeight:700 }}>
                        {f.facturaGenerada || f.ticketGenerado}
                      </span>
                    )}
                    {tipoKey === 'presupuestos' && f.estado !== 'Anulado' && (
                      <Btn size="sm" variant="success" onClick={() => onConvertirAFactura(f)}
                        title="Generar factura desde este presupuesto"
                        style={{ whiteSpace:'nowrap' }}>
                        📄 → Factura
                      </Btn>
                    )}
                    {tipoKey === 'presupuestos' && f.estado !== 'Anulado' && f.estado !== 'Aceptado' && (
                      <Btn size="sm" variant="success" onClick={() => onPasarAPedidoCliente(f)}
                        title="Convertir a pedido de cliente"
                        style={{ whiteSpace:'nowrap', background:'#7c3aed' }}>
                        🛒 → Pedido
                      </Btn>
                    )}
                    {tipoKey === 'proformas' && f.estado !== 'Anulada' && (
                      <Btn size="sm" variant="success" onClick={() => onConvertirAFactura(f)}
                        title="Generar factura desde esta proforma"
                        style={{ whiteSpace:'nowrap' }}>
                        📄 → Factura
                      </Btn>
                    )}
                    <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} title="Eliminar" onClick={() => setConfirm(f.id)}>🗑️</Btn>
                  </div>
                </td>
              </tr>
            );
          })}
          {!shownItems.length && <tr><td colSpan={9} style={{ padding:30, textAlign:'center', color:theme.faint }}>No hay {cfg.label.toLowerCase()}</td></tr>}
        </tbody>
      </table>
    </div>
  );
});

// Pago mixto: reparte el total de la factura entre varias formas de pago
// (ej. parte efectivo, parte tarjeta). No deja guardar si la suma de las
// filas no cuadra exactamente con el total.
function PagoMixtoModal({ total, filasIniciales, onGuardar, onCancelar }) {
  const [filas, setFilas] = useState(filasIniciales && filasIniciales.length ? filasIniciales : [{ modo:'Efectivo', importe:'' }, { modo:'Tarjeta', importe:'' }]);
  const suma = filas.reduce((s, f) => s + (parseFloat(f.importe) || 0), 0);
  const diff = total - suma;
  const cuadra = Math.abs(diff) < 0.005;

  function actualizar(i, campo, val) { setFilas(fs => fs.map((f, j) => j === i ? { ...f, [campo]: val } : f)); }
  function quitar(i) { setFilas(fs => fs.filter((_, j) => j !== i)); }
  function anadir() { setFilas(fs => [...fs, { modo:'Efectivo', importe:'' }]); }

  return (
    <Modal title="Pago mixto" onClose={onCancelar} width={440}>
      <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
        {filas.map((f, i) => (
          <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
            <div style={{ flex:1 }}><Select label="Modo" value={f.modo} onChange={v=>actualizar(i,'modo',v)} options={PAGOS} sorted={false} /></div>
            <div style={{ width:120 }}><Input label="Importe (€)" type="number" value={f.importe} onChange={v=>actualizar(i,'importe',v)} /></div>
            {filas.length > 1 && <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} onClick={()=>quitar(i)}>🗑️</Btn>}
          </div>
        ))}
        <Btn variant="ghost" size="sm" onClick={anadir}>+ Añadir forma de pago</Btn>
      </div>
      <div style={{ fontSize:13, marginBottom:14, color: cuadra ? '#166534' : '#991b1b' }}>
        Suma: <b>{formatEur(suma)}</b> de <b>{formatEur(total)}</b>
        {cuadra ? ' ✓' : diff > 0 ? ` (faltan ${formatEur(diff)})` : ` (sobran ${formatEur(-diff)})`}
      </div>
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
        <Btn variant="outline" onClick={onCancelar}>Cancelar</Btn>
        <Btn variant="primary" disabled={!cuadra} onClick={() => onGuardar(filas.map(f => ({ modo:f.modo, importe:parseFloat(f.importe)||0 })))}>Guardar</Btn>
      </div>
    </Modal>
  );
}

export default function FacturasModule({ tipoKey = 'facturas' }) {
  const theme = useT();
  const { data, addItem, replaceItem, deleteItem, nextNum } = useStore();
  const cfg  = TIPO_CONFIG[tipoKey];
  const { config, clientes, articulos } = data;
  const items = data[tipoKey] || [];
  const { q, setQ, filtered } = useSearch(items, ['num','clienteNombre','estado','clienteNif','observaciones'], tipoKey);
  const { sorted: sortedItems, sortField, sortDir, handleSort } = useSortable(filtered, 'num', 'desc');
  const [modal, setModal]     = useState(null);
  const [pagoMixtoModal, setPagoMixtoModal] = useState(false);
  const [form, setForm]       = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [exportModal, setExportModal] = useState(false);
  const [scanEnvio, setScanEnvio]     = useState(null); // null | 'scanning' | 'sending'
  const [scanModal, setScanModal]     = useState(false);
  const [scanDoc,   setScanDoc]       = useState(null);
  const [scanEmail, setScanEmail]     = useState('');
  const [scanPreview, setScanPreview] = useState(null);   // { scanId: string, img: string (base64 jpeg) }

  const [importModal, setImportModal] = useState(false);
  const [importLineas, setImportLineas] = useState([]);
  const [importMeta, setImportMeta] = useState({});
  const [importError, setImportError] = useState('');
  const [filtroEstados, setFiltroEstados] = useLocalStorage('filtroEstados_' + tipoKey, []);
  const [expDesde, setExpDesde] = useState('');
  const [expHasta, setExpHasta] = useState('');

  const year     = new Date().getFullYear();
  const mesActual = today().slice(0,7);
  const totalMes = items.filter(f => f.fecha.slice(0,7) === mesActual);
  const cobradas = items.filter(f => f.estado === 'Cobrada');
  const pendientes = items.filter(f => f.estado === 'Pendiente');
  const importeMes = totalMes.filter(f => f.estado !== 'Anulada' && f.estado !== 'Anulado').reduce((s,f) => s + calcLineas(f.lineas).total, 0);

  function openNew()   { setForm(newDoc(cfg.tipo, config)); setModal('form'); }

  async function leerExcel(file) {
    setImportError('');
    setImportLineas([]);
    try {
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type: 'array' });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      // Busca la fila de cabecera con CONCEPTO / PVP / UND
      let headerIdx = -1;
      let colDesc = -1, colPrecio = -1, colCant = -1, colIvaRow = -1;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i].map(c => String(c).toUpperCase().trim());
        const dI = row.findIndex(c => c === 'CONCEPTO' || c === 'DESCRIPCION' || c === 'DESCRIPCIÓN');
        if (dI >= 0) {
          headerIdx = i;
          colDesc   = dI;
          colPrecio = row.findIndex(c => /PVP|PRECIO/.test(c));
          colCant   = row.findIndex(c => /^UND$|^CANT|^UNID|^QTY/.test(c));
          if (colPrecio < 0) colPrecio = row.findIndex(c => /IMPORT/.test(c));
          break;
        }
      }
      if (headerIdx < 0) { setImportError('No se encontró la fila de cabecera (CONCEPTO / PVP / UND).'); return; }

      // Busca el IVA en las filas de totales (después de los datos)
      let ivaGlobal = config.iva || 21;
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const row = rows[i].map(c => String(c).toUpperCase().trim());
        const ivaIdx = row.findIndex(c => /^IVA/.test(c));
        if (ivaIdx >= 0) {
          // El valor IVA puede ser 0.21 o 21 — busca el primer número en la fila
          for (let j = 0; j < rows[i].length; j++) {
            const v = parseFloat(rows[i][j]);
            if (!isNaN(v) && v > 0) {
              ivaGlobal = v <= 1 ? Math.round(v * 100) : v;
              break;
            }
          }
          break;
        }
      }

      // Extrae líneas con descripción y precio > 0
      const lineas = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const row = rows[i];
        const desc = String(row[colDesc] ?? '').trim() || String(row[1] ?? '').trim();
        if (!desc) continue;
        const precio = colPrecio >= 0 ? parseFloat(row[colPrecio]) || 0 : 0;
        const cant   = colCant   >= 0 ? parseFloat(row[colCant])   || 1 : 1;
        // Para filas que usan col 1 como desc y col 6 como precio (formato Root)
        const precioAlt = parseFloat(row[6]) || 0;
        const cantAlt   = parseFloat(row[7]) || 1;
        const precioFinal = precio || precioAlt;
        const cantFinal   = precio ? cant : cantAlt;
        if (!precioFinal && !desc) continue;
        // Cortar al llegar a fila de datos bancarios (busca en toda la fila)
        const rowStr = row.map(c => String(c)).join(' ');
        if (/datos bancarios|ingreso.{0,5}transferencia/i.test(rowStr)) break;
        if (/total|iva|base/i.test(desc)) continue;
        lineas.push({ desc, cant: cantFinal, precio: precioFinal, iva: ivaGlobal });
      }

      if (!lineas.length) { setImportError('No se encontraron líneas con datos.'); return; }

      // Extrae metadatos del encabezado (cliente, NIF, forma de pago, fecha)
      const meta = {};
      for (let i = 0; i < Math.min(headerIdx, rows.length); i++) {
        const row = rows[i];
        for (let j = 0; j < row.length; j++) {
          const key = String(row[j]).toUpperCase().trim();
          const val = String(row[j+1] ?? '').trim();
          if (/NIF|CIF/.test(key) && val) meta.clienteNif = val.replace(/CIF\/NIF:\s*/i,'').trim();
          if (/FECHA/.test(key)) {
            const v = row[j+1];
            if (typeof v === 'number') {
              const d = new Date(Math.round((v - 25569) * 86400 * 1000));
              meta.fecha = d.toISOString().slice(0,10);
            } else if (val) meta.fecha = val;
          }
          if (/PAGO/.test(key) && val) {
            const u = val.toUpperCase().trim();
            meta.pago = u === 'TARJETA' ? 'Tarjeta' : u === 'EFECTIVO' ? 'Efectivo' : u === 'TRANSFERENCIA' ? 'Transferencia' : u === 'BIZUM' ? 'Bizum' : val;
          }
        }
        // Cliente suele estar en col 6 de la fila del número de factura
        const numIdx = row.findIndex(c => /N[ºÚ]\s*FACTURA|FACTURA/i.test(String(c)));
        if (numIdx >= 0 && row[6]) meta.clienteNombre = String(row[6]).trim();
        // NIF en misma fila
        const nifStr = row.find(c => /CIF\/NIF:/i.test(String(c)));
        if (nifStr) {
          const nxt = row[row.indexOf(nifStr)+1];
          if (nxt) meta.clienteNif = String(nxt).trim();
        }
      }
      setImportMeta(meta);
      setImportLineas(lineas);
    } catch(e) { setImportError('Error leyendo el archivo: ' + e.message); }
  }

  function descargarPdf(doc) {
    const etiq = tipoKey === 'presupuestos' ? 'presupuesto' : 'factura';
    fetch(`/api/pdf/documento/${tipoKey}/${doc.id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}` }
    }).then(r => r.blob()).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${etiq}-${doc.num}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    });
  }

  function enviarEmailDoc(doc) {
    const cli = clientes.find(c => String(c.id) === String(doc.clienteId));
    const actual = doc.clienteEmail || cli?.email || '';
    const para = window.prompt(`Enviar email a ${doc.clienteNombre || 'cliente'}:`, actual);
    if (!para || !para.trim()) return;
    const paraFinal = para.trim();
    if (cli && paraFinal !== cli.email) replaceItem('clientes', { ...cli, email: paraFinal });
    if (paraFinal !== doc.clienteEmail) replaceItem(tipoKey, { ...doc, clienteEmail: paraFinal });
    fetch(`/api/email/documento/${tipoKey}/${doc.id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ para: paraFinal }),
    }).then(r => r.json()).then(d => { if (d.ok) alert(`✅ Email enviado a ${doc.clienteNombre || 'cliente'} (${paraFinal})`); else alert('❌ ' + d.error); })
    .catch(e => alert('❌ ' + e.message));
  }

  function enviarWhatsappDoc(doc) {
    const cli = clientes.find(c => String(c.id) === String(doc.clienteId));
    const actual = doc.clienteTel || cli?.telefono || '';
    const tel = window.prompt(`Enviar WhatsApp a ${doc.clienteNombre || 'cliente'}:`, actual);
    if (!tel || !tel.trim()) return;
    let d = tel.trim().replace(/\D/g, '');
    if (d.startsWith('00')) d = d.slice(2);
    if (d.length === 9) d = '34' + d;

    const nombreCliente = (doc.clienteNombre || '').split(' ')[0] || 'cliente';
    const mensajeDefecto = tipoKey === 'presupuestos'
      ? `Hola ${nombreCliente}, te adjuntamos el presupuesto solicitado.\n\nRoot Informática`
      : tipoKey === 'proformas'
      ? `Hola ${nombreCliente}, te adjuntamos la proforma solicitada.\n\nRoot Informática`
      : '';
    const mensaje = mensajeDefecto ? window.prompt('Mensaje a incluir:', mensajeDefecto) : null;
    if (mensaje === null && mensajeDefecto) return;

    fetch(`/api/whatsapp/documento/${tipoKey}/${doc.id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: d, mensaje: mensaje || '' }),
    }).then(r => r.json()).then(r => { if (r.ok) alert(`✅ Enviado por WhatsApp a ${doc.clienteNombre || 'cliente'} (${tel.trim()})`); else alert('❌ ' + r.error); })
    .catch(e => alert('❌ ' + e.message));
  }

  function enviarWhatsappPedidoListo(doc) {
    const cli = clientes.find(c => String(c.id) === String(doc.clienteId));
    const actual = doc.clienteTel || cli?.telefono || '';
    const tel = window.prompt(`Enviar WhatsApp a ${doc.clienteNombre || 'cliente'}:`, actual);
    if (!tel || !tel.trim()) return;
    let d = tel.trim().replace(/\D/g, '');
    if (d.startsWith('00')) d = d.slice(2);
    if (d.length === 9) d = '34' + d;

    fetch(`/api/whatsapp/pedido-cliente/${doc.id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('ri_token')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ to: d }),
    }).then(r => r.json()).then(r => { if (r.ok) alert(`✅ Enviado por WhatsApp a ${doc.clienteNombre || 'cliente'} (${tel.trim()})`); else alert('❌ ' + r.error); })
    .catch(e => alert('❌ ' + e.message));
  }

  async function escanear() {
    const token = localStorage.getItem('ri_token');
    setScanEnvio('scanning');
    setScanPreview(null);
    try {
      const r = await fetch('/api/scanner/escanear', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ resolucion: 200, modo: 'Gray', fuente: 'Flatbed' }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error escaneando');
      setScanPreview({ scanId: d.scanId, img: d.preview });
    } catch(e) { alert('❌ Escáner: ' + e.message); }
    setScanEnvio(null);
  }

  async function enviarEscaneado() {
    const token = localStorage.getItem('ri_token');
    setScanEnvio('sending');
    try {
      const r = await fetch(`/api/email/documento/${tipoKey}/${scanDoc.id}`, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' },
        body: JSON.stringify({ para: scanEmail, scanId: scanPreview.scanId }),
      });
      const d = await r.json();
      if (d.ok) alert(`✅ Enviado a ${scanDoc?.clienteNombre ? scanDoc.clienteNombre + ' (' + scanEmail + ')' : scanEmail}`);
      else alert('❌ ' + d.error);
    } catch(e) { alert('❌ Email: ' + e.message); }
    setScanEnvio(null);
    setScanModal(false);
    setScanPreview(null);
  }

  function confirmarImport() {
    const doc = { ...newDoc(cfg.tipo, config), ...importMeta, lineas: importLineas };
    setForm(doc);
    setModal('form');
    setImportModal(false);
    setImportLineas([]);
    setImportMeta({});
  }
  function openEdit(f) { setForm({...f, lineas: f.lineas.map(l=>({...l}))}); setModal('form'); }
  function duplicar(f) {
    const base = newDoc(cfg.tipo, config);
    setForm({ ...base, clienteId:f.clienteId, clienteNombre:f.clienteNombre, clienteNif:f.clienteNif||'', clienteDireccion:f.clienteDireccion||'', clienteTel:f.clienteTel||clientes.find(c=>String(c.id)===String(f.clienteId))?.telefono||'', pago:f.pago, lineas:f.lineas.map(l=>({...l})) });
    setModal('form');
  }

  function convertirAFactura(presup) {
    // Abre el modal de nueva factura pre-rellenada con los datos del presupuesto
    const nuevaFactura = {
      id: null,
      tipo: 'FACTURA',
      num: '',
      fecha: today(),
      clienteId:       presup.clienteId       || '',
      clienteNombre:   presup.clienteNombre    || '',
      clienteNif:      presup.clienteNif       || '',
      clienteDireccion:presup.clienteDireccion || '',
      clienteTel:      presup.clienteTel || clientes.find(c => String(c.id) === String(presup.clienteId))?.telefono || '',
      pago:            'Tarjeta',
      validez:         '',
      lineas:          presup.lineas.map(l => ({...l})),
      estado:          'Cobrada',
      _origenPresupuesto: presup.num,   // referencia al presupuesto origen
    };
    setForm(nuevaFactura);
    setModal('convertir');
  }

  function saveConvertir() {
    if (!form.clienteNombre) return alert('Introduce el nombre del cliente.');
    if (!form.clienteNif?.trim()) return alert('El NIF/CIF del cliente es obligatorio para facturar.');
    if (!form.lineas.length || !form.lineas[0].desc) return alert('Añade al menos una línea.');

    let clienteIdFinal = form.clienteId;
    if (form.clienteNombre && !form.clienteId) {
      const existe = clientes.find(c => c.nombre.toLowerCase() === form.clienteNombre.toLowerCase());
      if (!existe) {
        const nuevo = { id: Date.now(), nombre: form.clienteNombre, nif: form.clienteNif||'', telefono: form.clienteTel||'', email:'', direccion: form.clienteDireccion||'' };
        addItem('clientes', nuevo);
        clienteIdFinal = nuevo.id;
      } else { clienteIdFinal = existe.id; }
    }
    const clienteTelFinal = form.clienteTel || clientes.find(c => String(c.id) === String(clienteIdFinal))?.telefono || '';

    const n = config.sigFactura;
    const numFactura = `${n}_${new Date().getFullYear()}`;
    nextNum('sigFactura', '');
    const nuevaFactura = { ...form, id: n, num: numFactura, clienteId: clienteIdFinal, clienteTel: clienteTelFinal };
    const { _origenPresupuesto, ...facturaLimpia } = nuevaFactura;
    addItem('facturas', facturaLimpia);

    // Marcar el presupuesto como Aceptado
    const presups = data.presupuestos;
    const presupOrigen = presups.find(p => p.num === form._origenPresupuesto);
    if (presupOrigen) replaceItem('presupuestos', { ...presupOrigen, estado: 'Aceptado', facturaGenerada: facturaLimpia.num });

    setModal(null);
    // Notificar
    setTimeout(() => alert(`✓ Factura ${facturaLimpia.num} creada correctamente.\nEl presupuesto ${form._origenPresupuesto || ''} queda marcado como Aceptado.`), 100);
  }

  function handleNuevoCliente(nuevo)  { addItem('clientes', nuevo); }
  function handleEditarCliente(cli)   { replaceItem('clientes', cli); }
  function handleClienteChange({ id, nombre, nif, direccion, telefono }) {
    setForm(f => ({ ...f, clienteId: id, clienteNombre: nombre, clienteNif: nif||'', clienteDireccion: direccion||'', clienteTel: telefono||'' }));
  }

  function buildDoc() {
    if (!form.clienteNombre) { alert('Introduce el nombre del cliente.'); return null; }
    if (tipoKey === 'facturas' && !form.clienteNif?.trim()) { alert('El NIF/CIF del cliente es obligatorio para facturar.'); return null; }
    if (!form.lineas.length || !form.lineas[0].desc) { alert('Añade al menos una línea.'); return null; }
    let clienteIdFinal = form.clienteId;
    if (form.clienteNombre && !form.clienteId) {
      const existe = clientes.find(c => c.nombre.toLowerCase() === form.clienteNombre.toLowerCase());
      if (!existe) {
        const nuevo = { id: Date.now(), nombre: form.clienteNombre, nif: form.clienteNif||'', telefono: form.clienteTel||'', email:'', direccion: form.clienteDireccion||'' };
        addItem('clientes', nuevo);
        clienteIdFinal = nuevo.id;
      } else {
        clienteIdFinal = existe.id;
      }
    }
    const clienteTelFinal = form.clienteTel || clientes.find(c => String(c.id) === String(clienteIdFinal))?.telefono || '';
    const formFinal = { ...form, clienteId: clienteIdFinal, clienteTel: clienteTelFinal };
    if (form.id) {
      replaceItem(tipoKey, formFinal);
      return formFinal;
    } else {
      const n = config[cfg.sig];
      nextNum(cfg.sig, '');
      const doc = { ...formFinal, id: n, num: cfg.fmt(n, year) };
      addItem(tipoKey, doc);
      return doc;
    }
  }

  function save() {
    if (buildDoc()) setModal(null);
  }

  function saveEImprimirA4() {
    const doc = buildDoc();
    if (doc) { setModal(null); setTimeout(() => printFactura({...doc, tipo:cfg.tipo}, config), 150); }
  }

  function saveEImprimirTicket() {
    const doc = buildDoc();
    if (doc) {
      setModal(null);
      setTimeout(() => printTicket({
        ...doc,
        tipo: cfg.tipo,
        pago: doc.pago || 'Pendiente',
        clienteNombre: doc.clienteNombre,
      }, config), 150);
    }
  }

  function pasarAPedidoCliente(presup) {
    const n = config.sigPedidoCliente;
    nextNum('sigPedidoCliente');
    const num = `PC-${n}_${year}`;
    const pedido = {
      ...presup,
      id: Date.now(),
      num,
      tipo: 'PEDIDO CLIENTE',
      estado: 'Pendiente',
      facturaGenerada: '',
      ticketGenerado: '',
    };
    addItem('pedidosCliente', pedido);
    replaceItem('presupuestos', { ...presup, estado: 'Aceptado', facturaGenerada: num });
    setTimeout(() => alert(`✓ Pedido ${num} creado desde presupuesto ${presup.num}`), 100);
  }

  function pasarAFactura(pedido) {
    if (!pedido.clienteNif?.trim()) return alert('El NIF/CIF del cliente es obligatorio para facturar. Añádelo en el pedido antes de generar la factura.');
    const n = config.sigFactura;
    nextNum('sigFactura', '');
    const factura = {
      ...pedido,
      id: n,
      num: `${n}_${year}`,
      fecha: today(),
      tipo: 'FACTURA',
      estado: 'Cobrada',
      _origenPedido: pedido.num,
    };
    const { _origenPedido, ...facturaLimpia } = factura;
    addItem('facturas', facturaLimpia);
    replaceItem(tipoKey, { ...pedido, estado: 'Entregado al cliente', facturaGenerada: facturaLimpia.num });
    setTimeout(() => alert(`✓ Factura ${facturaLimpia.num} creada desde pedido ${pedido.num}`), 100);
  }

  function pasarATicket(pedido) {
    const n = config.sigTicket;
    nextNum('sigTicket', '');
    const num = `T-${String(n).padStart(4,'0')}`;
    const ticket = {
      id: n, num,
      fecha: today(),
      clienteId: pedido.clienteId,
      clienteNombre: pedido.clienteNombre,
      pago: pedido.pago || 'Efectivo',
      lineas: pedido.lineas.map(l => ({...l})),
      estado: 'Cobrado',
    };
    addItem('tickets', ticket);
    replaceItem(tipoKey, { ...pedido, estado: 'Entregado al cliente', ticketGenerado: num });
    setTimeout(() => termicaPedido({...ticket, tipo:'PEDIDO CLIENTE'}).catch(() => printTicket({...ticket, tipo:'PEDIDO CLIENTE'}, config)), 150);
    setTimeout(() => alert(`✓ Ticket ${num} creado desde pedido ${pedido.num}`), 400);
  }

  const { base, iva, total } = form ? calcLineas(form.lineas) : { base:0, iva:0, total:0 };
  const isFactura = tipoKey === 'facturas';

  // Wrappers de referencia estable (useCallback + ref con los handlers reales) para que
  // TablaDocumentos (memo) no se re-renderice en cada tecla del formulario del modal —
  // sin tener que auditar a mano las dependencias de cada handler de abajo.
  const __handlers = useRef();
  __handlers.current = { openEdit, descargarPdf, enviarEmailDoc, enviarWhatsappDoc, enviarWhatsappPedidoListo, duplicar, pasarAFactura, pasarATicket, convertirAFactura, pasarAPedidoCliente };
  const onEdit                     = useCallback(f => __handlers.current.openEdit(f), []);
  const onDescargarPdf             = useCallback(f => __handlers.current.descargarPdf(f), []);
  const onEnviarEmail              = useCallback(f => __handlers.current.enviarEmailDoc(f), []);
  const onEnviarWhatsapp           = useCallback(f => __handlers.current.enviarWhatsappDoc(f), []);
  const onEnviarWhatsappPedidoListo= useCallback(f => __handlers.current.enviarWhatsappPedidoListo(f), []);
  const onDuplicar                 = useCallback(f => __handlers.current.duplicar(f), []);
  const onPasarAFactura            = useCallback(f => __handlers.current.pasarAFactura(f), []);
  const onPasarATicket             = useCallback(f => __handlers.current.pasarATicket(f), []);
  const onConvertirAFactura        = useCallback(f => __handlers.current.convertirAFactura(f), []);
  const onPasarAPedidoCliente      = useCallback(f => __handlers.current.pasarAPedidoCliente(f), []);

  const shownItems = useMemo(
    () => (filtroEstados.length ? sortedItems.filter(f => filtroEstados.includes(f.estado)) : sortedItems),
    [sortedItems, filtroEstados]
  );

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:`repeat(${isFactura?4:3},1fr)`, gap:10, marginBottom:16 }}>
        <StatCard label={`${cfg.label} este mes`} value={totalMes.length} />
        {isFactura && <StatCard label="Cobradas"  value={cobradas.length}  color="#0e9f6e" />}
        {isFactura && <StatCard label="Pendientes" value={pendientes.length} color="#c27803" />}
        <StatCard label={isFactura?"Facturado mes":"Total"} value={formatEur(importeMes)} color="#1a56db" />
      </div>

      <SearchBar value={q} onChange={setQ} placeholder={`Buscar ${cfg.label.toLowerCase()}...`}>
        <MultiSelect options={isFactura ? ESTADOS : tipoKey==='pedidosCliente' ? ESTADOS_PEDIDO_CLI : ESTADOS_PRESUP} selected={filtroEstados} onChange={setFiltroEstados} placeholder="Estado: Todos" />
        {isFactura && <Btn variant="ghost" title="Exportar listado a Excel" onClick={() => setExportModal(true)}>📊 Exportar Excel</Btn>}
        {(tipoKey === 'presupuestos' || isFactura) && <Btn variant="ghost" title="Importar líneas desde archivo Excel" onClick={() => { setImportLineas([]); setImportError(''); setImportModal(true); }}>📥 Importar Excel</Btn>}
        {(tipoKey === 'facturas' || tipoKey === 'presupuestos' || tipoKey === 'proformas') && (
          <Btn variant="ghost" style={{ ...pastelBtn(theme,'green') }} title="Escanear documento y enviarlo por email"
            disabled={!!scanEnvio}
            onClick={() => { setScanDoc(null); setScanEmail(''); setScanModal(true); }}>
            {scanEnvio === 'scanning' ? '⏳ Escaneando...' : scanEnvio === 'sending' ? '📤 Enviando...' : '🖨️✉️ Escanear y enviar'}
          </Btn>
        )}
        <Btn variant="primary" onClick={openNew} title={`Crear nueva ${cfg.singular.toLowerCase()}`}>+ Nueva {cfg.singular.toLowerCase()}</Btn>
      </SearchBar>

      <TablaDocumentos
        shownItems={shownItems} sortField={sortField} sortDir={sortDir} handleSort={handleSort} theme={theme}
        tipoKey={tipoKey} cfg={cfg} config={config} replaceItem={replaceItem} setConfirm={setConfirm}
        onEdit={onEdit} onDescargarPdf={onDescargarPdf} onEnviarEmail={onEnviarEmail} onEnviarWhatsapp={onEnviarWhatsapp}
        onEnviarWhatsappPedidoListo={onEnviarWhatsappPedidoListo} onDuplicar={onDuplicar}
        onPasarAFactura={onPasarAFactura} onPasarATicket={onPasarATicket}
        onConvertirAFactura={onConvertirAFactura} onPasarAPedidoCliente={onPasarAPedidoCliente}
      />

      {/* ── MODAL NUEVO / EDITAR ── */}
      {modal === 'form' && form && (
        <Modal title={form.id ? `Editar ${form.num}` : `Nueva ${cfg.singular.toLowerCase()}`} onClose={() => setModal(null)} width={720}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            {form.id && <Input label="Número" value={form.num} readOnly />}
            <Input label="Fecha" type="date" value={form.fecha} onChange={v=>setForm(f=>({...f,fecha:v}))} />

            {/* Selector cliente con botón nuevo — fila completa */}
            <div style={{ gridColumn:'1/-1' }}>
              <ClienteSelector
                clientes={clientes}
                clienteId={form.clienteId}
                clienteNombre={form.clienteNombre}
                onChange={handleClienteChange}
                onNuevoCliente={handleNuevoCliente}
                onEditarCliente={handleEditarCliente}
              />
            </div>

            <Input label="NIF / CIF del cliente"   value={form.clienteNif}       onChange={v=>setForm(f=>({...f,clienteNif:v}))} />
            {tipoKey === 'pedidosCliente' && <Input label="Teléfono" value={form.clienteTel||''} onChange={v=>setForm(f=>({...f,clienteTel:v}))} placeholder="600 000 000" />}
            {tipoKey === 'pedidosCliente' && <Input label="Señal entregada (€)" type="number" value={form.senal||''} onChange={v=>setForm(f=>({...f,senal:v}))} />}
            <Input label="Dirección de facturación" value={form.clienteDireccion} onChange={v=>setForm(f=>({...f,clienteDireccion:v}))} />
            <Select label="Forma de pago" value={form.pago}
              onChange={v => v === 'Mixto' ? setPagoMixtoModal(true) : setForm(f=>({...f,pago:v}))}
              options={tipoKey === 'facturas' ? PAGOS_CON_MIXTO : PAGOS} />
            {form.pago === 'Mixto' && (form.pagoMixtoDetalle||[]).length > 0 && (
              <div style={{ gridColumn:'1/-1', fontSize:12, color:theme.muted, marginTop:-6 }}>
                {form.pagoMixtoDetalle.map(d => `${d.modo}: ${formatEur(d.importe)}`).join(' · ')}
                {' '}<button type="button" onClick={()=>setPagoMixtoModal(true)} style={{ background:'none', border:'none', color:'#1a56db', cursor:'pointer', fontSize:12, padding:0 }}>✏️ editar</button>
              </div>
            )}
            {tipoKey !== 'facturas' && <Input label="Válida hasta" type="date" value={form.validez} onChange={v=>setForm(f=>({...f,validez:v}))} />}
            <Select label="Estado" value={form.estado} onChange={v=>setForm(f=>({...f,estado:v}))} options={tipoKey==='presupuestos' ? ESTADOS_PRESUP : tipoKey==='pedidosCliente' ? ESTADOS_PEDIDO_CLI : ESTADOS} />
          </div>

          <div style={{ marginTop:12 }}>
            <label style={{ fontSize:12, fontWeight:600, color:theme.body, display:'block', marginBottom:4 }}>Observaciones</label>
            <textarea rows={3} value={form.observaciones||''} onChange={e=>setForm(f=>({...f,observaciones:e.target.value}))}
              style={{ width:'100%', fontSize:13, border:`1px solid ${theme.borderStrong}`, borderRadius:6, padding:'8px 10px', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box' }}
              placeholder="Notas o condiciones adicionales..." />
          </div>

          <LineasEditor lineas={form.lineas} onChange={l=>setForm(f=>({...f,lineas:l}))} articulos={articulos} />

          {(tipoKey === 'facturas' || tipoKey === 'tickets') && form?.id && (
            <VFDetalle
              doc={form}
              col={tipoKey}
              onReenviado={() => { const updated = items.find(x => x.id === form.id); if (updated) setForm(updated); }}
            />
          )}

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginTop:12, paddingTop:12, borderTop:`1px solid ${theme.border}` }}>
            <div style={{ fontSize:12, color:theme.muted }}>
              Base: <b>{formatEur(base)}</b> · IVA: <b>{formatEur(iva)}</b> ·{' '}
              <span style={{ fontSize:15, fontWeight:700, color:'#1a56db' }}>Total: {formatEur(total)}</span>
              {tipoKey === 'pedidosCliente' && parseFloat(form.senal) > 0 && (
                <span style={{ marginLeft:12 }}>
                  · Señal: <b style={{ color:'#0e9f6e' }}>{formatEur(parseFloat(form.senal))}</b>
                  · Resto: <b style={{ color:'#c27803' }}>{formatEur(Math.max(0, total - parseFloat(form.senal)))}</b>
                </span>
              )}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn variant="outline" onClick={() => setModal(null)} title="Descartar cambios">Cancelar</Btn>
              {tipoKey === 'pedidosCliente'
                ? <>
                    <Btn variant="ghost" onClick={saveEImprimirA4} title="Guardar e imprimir en A4">🖨️ A4</Btn>
                    <Btn variant="ghost" onClick={saveEImprimirTicket} title="Guardar e imprimir ticket térmico">🖨️ Ticket</Btn>
                  </>
                : <Btn variant="ghost" onClick={saveEImprimirA4} title="Guardar e imprimir en A4">🖨️ Guardar e imprimir</Btn>
              }
              {(tipoKey === 'facturas' || tipoKey === 'presupuestos' || tipoKey === 'proformas') && form?.id &&
                <Btn variant="ghost" title="Guardar y descargar PDF" onClick={() => { const doc = buildDoc(); if (doc) descargarPdf(doc); }}>📄 PDF</Btn>
              }
              {(tipoKey === 'facturas' || tipoKey === 'presupuestos' || tipoKey === 'proformas') && form?.id &&
                <Btn variant="ghost" title="Guardar y enviar por email al cliente" onClick={() => { const doc = buildDoc(); if (doc) enviarEmailDoc(doc); }}>✉️ Email</Btn>
              }
              <Btn variant="primary" onClick={save} title="Guardar sin imprimir">Guardar</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL CONVERTIR A FACTURA ── */}
      {modal === 'convertir' && form && (
        <Modal title={`Convertir ${form._origenPresupuesto} → Factura`} onClose={() => setModal(null)} width={720}>
          <div style={{ background:'#f0f9ff', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:12, color:'#0369a1' }}>
            <b>📋 Origen: {form._origenPresupuesto}</b> — Revisa y ajusta los datos antes de generar la factura. Las líneas ya están copiadas del presupuesto.
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
            <Input label="Fecha factura" type="date" value={form.fecha} onChange={v=>setForm(f=>({...f,fecha:v}))} />
            <Select label="Forma de pago" value={form.pago}
              onChange={v => v === 'Mixto' ? setPagoMixtoModal(true) : setForm(f=>({...f,pago:v}))}
              options={PAGOS_CON_MIXTO} />
            {form.pago === 'Mixto' && (form.pagoMixtoDetalle||[]).length > 0 && (
              <div style={{ gridColumn:'1/-1', fontSize:12, color:theme.muted, marginTop:-6 }}>
                {form.pagoMixtoDetalle.map(d => `${d.modo}: ${formatEur(d.importe)}`).join(' · ')}
                {' '}<button type="button" onClick={()=>setPagoMixtoModal(true)} style={{ background:'none', border:'none', color:'#1a56db', cursor:'pointer', fontSize:12, padding:0 }}>✏️ editar</button>
              </div>
            )}
            <div style={{ gridColumn:'1/-1' }}>
              <ClienteSelector
                clientes={clientes}
                clienteId={form.clienteId}
                clienteNombre={form.clienteNombre}
                onChange={({ id, nombre, nif, direccion, telefono }) => setForm(f=>({ ...f, clienteId:id, clienteNombre:nombre, clienteNif:f.clienteNif||nif, clienteDireccion:f.clienteDireccion||direccion, clienteTel:telefono||'' }))}
                onNuevoCliente={handleNuevoCliente}
                onEditarCliente={handleEditarCliente}
              />
            </div>
            <Input label="NIF / CIF" value={form.clienteNif} onChange={v=>setForm(f=>({...f,clienteNif:v}))} />
            <Input label="Dirección" value={form.clienteDireccion} onChange={v=>setForm(f=>({...f,clienteDireccion:v}))} />
          </div>
          <LineasEditor lineas={form.lineas} onChange={l=>setForm(f=>({...f,lineas:l}))} articulos={articulos} />
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginTop:12, paddingTop:12, borderTop:`1px solid ${theme.border}` }}>
            <div style={{ fontSize:12, color:theme.muted }}>
              {(() => { const t = calcLineas(form.lineas); return <>Base: <b>{formatEur(t.base)}</b> · IVA: <b>{formatEur(t.iva)}</b> · <span style={{fontSize:15,fontWeight:700,color:'#1a56db'}}>Total: {formatEur(t.total)}</span></>; })()}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn variant="outline" onClick={() => setModal(null)} title="Cancelar">Cancelar</Btn>
              <Btn variant="ghost" title="Crear factura e imprimir en A4" onClick={() => { const f = saveConvertir(); if(f) setTimeout(() => printFactura({...f, tipo:'FACTURA'}, config), 300); }}>🖨️ Crear e imprimir</Btn>
              <Btn variant="success" onClick={saveConvertir} title="Crear factura sin imprimir">📄 Crear factura</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* ── MODAL EXPORTAR ── */}
      {exportModal && (
        <Modal title="Exportar facturas a Excel" onClose={() => setExportModal(false)} width={420}>
          <p style={{ fontSize:13, color:theme.muted, marginBottom:16 }}>Rango de fechas. Vacío = todos los registros.</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <Input label="Desde" type="date" value={expDesde} onChange={setExpDesde} />
            <Input label="Hasta" type="date" value={expHasta} onChange={setExpHasta} />
          </div>
          <div style={{ background:'#f0f9ff', borderRadius:8, padding:10, marginBottom:16, fontSize:12, color:'#0369a1' }}>
            {(() => {
              const n = items.filter(f => (!expDesde||f.fecha>=expDesde) && (!expHasta||f.fecha<=expHasta)).length;
              return `Se exportarán ${n} factura${n!==1?'s':''}`;
            })()}
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setExportModal(false)} title="Cancelar">Cancelar</Btn>
            <Btn variant="success" title="Descargar Excel con el rango seleccionado" onClick={() => { exportFacturas(items, expDesde, expHasta, config); setExportModal(false); }}>📥 Descargar Excel</Btn>
          </div>
        </Modal>
      )}

      {importModal && (
        <Modal title="Importar líneas desde Excel" onClose={() => setImportModal(false)} width={560}>
          <p style={{ color:theme.muted, fontSize:13, marginBottom:12 }}>
            Selecciona un archivo Excel (.xlsx / .xls). Se detectarán automáticamente las columnas de descripción, cantidad, precio e IVA. Se creará una nueva {cfg.singular.toLowerCase()} con esas líneas.
          </p>
          <input type="file" accept=".xlsx,.xls,.ods,.csv"
            onChange={e => e.target.files[0] && leerExcel(e.target.files[0])}
            style={{ marginBottom:12, display:'block' }} />
          {importError && <div style={{ color:'#dc2626', fontSize:13, marginBottom:8 }}>{importError}</div>}
          {importLineas.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontWeight:600, fontSize:13, marginBottom:6, color:theme.body }}>Vista previa — {importLineas.length} línea(s):</div>
              <div style={{ maxHeight:220, overflow:'auto', border:`1px solid ${theme.border}`, borderRadius:8 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead><tr style={{ background:theme.surface2 }}>
                    {['Descripción','Cant','Precio','IVA%'].map(h => <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:theme.muted, fontWeight:600 }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {importLineas.map((l,i) => (
                      <tr key={i} style={{ borderTop:`1px solid ${theme.border}` }}>
                        <td style={{ padding:'5px 10px' }}>{l.desc}</td>
                        <td style={{ padding:'5px 10px' }}>{l.cant}</td>
                        <td style={{ padding:'5px 10px' }}>{l.precio.toFixed(2)}</td>
                        <td style={{ padding:'5px 10px' }}>{l.iva}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => setImportModal(false)} title="Cancelar importación">Cancelar</Btn>
            <Btn variant="primary" onClick={confirmarImport} disabled={!importLineas.length} title="Crear documento con las líneas importadas">✅ Crear {cfg.singular.toLowerCase()}</Btn>
          </div>
        </Modal>
      )}

      {confirm && <Confirm msg={`¿Eliminar esta ${cfg.singular.toLowerCase()}?`} onOk={() => { deleteItem(tipoKey, confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />}

      {scanModal && (
        <Modal title="Escanear y enviar por email" onClose={() => { if (!scanEnvio) { setScanModal(false); setScanPreview(null); } }} width={'min(860px, 96vw)'} height={'90vh'}>
          <div style={{ display:'flex', flexDirection:'column', gap:12, flex:1, overflow:'hidden' }}>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em' }}>Documento</label>
                <select value={scanDoc?.id || ''} onChange={e => {
                  const d = items.find(x => String(x.id) === e.target.value);
                  setScanDoc(d || null);
                  setScanPreview(null);
                  if (d) {
                    const cli = clientes.find(c => String(c.id) === String(d.clienteId));
                    setScanEmail(d.clienteEmail || cli?.email || '');
                  }
                }} style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13 }}>
                  <option value="">— Seleccionar {cfg.singular.toLowerCase()} —</option>
                  {[...items].sort((a,b) => (b.num||'').localeCompare(a.num||'')).map(d => (
                    <option key={d.id} value={d.id}>{d.num} — {d.clienteNombre || '(sin cliente)'}</option>
                  ))}
                </select>
              </div>
              <Input label="Email destinatario" type="email" value={scanEmail} onChange={setScanEmail} placeholder="cliente@ejemplo.com" />
            </div>

            {scanEnvio === 'scanning' && (
              <div style={{ ...pastelBtn(theme,'blue'), borderRadius:8, padding:'10px 14px', fontSize:13 }}>⏳ Escaneando documento...</div>
            )}
            {scanEnvio === 'sending' && (
              <div style={{ ...pastelBtn(theme,'blue'), borderRadius:8, padding:'10px 14px', fontSize:13 }}>📤 Enviando email...</div>
            )}

            {scanPreview && !scanEnvio && (
              <div style={{ display:'flex', flexDirection:'column', gap:6, flex:1, overflow:'hidden' }}>
                <div style={{ fontSize:12, fontWeight:600, color:'#065f46', flexShrink:0 }}>✓ Escaneado — revisa el documento antes de enviarlo</div>
                {scanPreview.img
                  ? <div style={{ flex:1, overflow:'auto', border:`1px solid ${theme.border}`, borderRadius:8, background:theme.surface2, display:'flex', justifyContent:'center', alignItems:'flex-start' }}>
                      <img src={`data:image/jpeg;base64,${scanPreview.img}`} alt="Vista previa" style={{ maxWidth:'100%', height:'auto', display:'block' }} />
                    </div>
                  : <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:theme.muted, fontSize:13, border:`1px solid ${theme.border}`, borderRadius:8 }}>Vista previa no disponible — el PDF se enviará igualmente</div>
                }
              </div>
            )}

            {!scanPreview && !scanEnvio && (
              <div style={{ background:theme.surface2, border:'1px dashed #d1d5db', borderRadius:8, padding:'20px', textAlign:'center', color:theme.faint, fontSize:13 }}>
                Pulsa "Escanear" para capturar el documento desde la Brother DCP-7065DN
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', flexShrink:0, paddingTop:12 }}>
            <Btn variant="outline" onClick={() => { setScanModal(false); setScanPreview(null); }} disabled={!!scanEnvio}>Cancelar</Btn>
            <Btn variant="ghost" disabled={!scanDoc || !!scanEnvio} onClick={escanear}>🖨️ Escanear</Btn>
            <Btn variant="primary" disabled={!scanPreview || !scanDoc || !scanEmail || !!scanEnvio} onClick={enviarEscaneado}>✉️ Enviar</Btn>
          </div>
        </Modal>
      )}
      {pagoMixtoModal && form && (
        <PagoMixtoModal
          total={calcLineas(form.lineas).total}
          filasIniciales={form.pagoMixtoDetalle}
          onCancelar={() => setPagoMixtoModal(false)}
          onGuardar={filas => { setForm(f => ({ ...f, pago:'Mixto', pagoMixtoDetalle: filas })); setPagoMixtoModal(false); }}
        />
      )}
    </div>
  );
}
